import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useConvertLbEntry } from "src/generator/hooks/useConvertLbEntry";
import {
  useIsNewEntry,
  useIsOnline,
  usePostApi,
  useUploadFileApi,
} from "src/hooks";
import { type ICredentials } from "src/interfaces";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  setActiveAutosave,
  setCurrent,
  setNoSaves,
  setOpenedSaveModal,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";
import {
  ESaveEntryReason,
  refetchLogbook,
  setFiles,
  setSaveReason,
} from "src/redux/slices/generatorSlice";
import { getDateTimeRecord, crumb } from "src/utils";
import { Notification } from "src/utils/Notification";

import { getCurrentUTC } from "../../utils/timeUtils/getCurrentUTC";
import { usePendingFilesContext } from "../LogBar/PendingFilesContext";
import { mapUsers } from "../utils/mapUsers";
import { useIsPendingWithdraw } from "./useIsPendingWithdraw";
import { useSaveContactNumber } from "./useSaveContactNumber";
import { refreshLogoutTimer } from "../utils/refreshLogoutTimer";
// import { setWasSaved } from "../../redux/slices/generatorFeaturesSlice";
/**
 * Custom hook to handle saving an entry in the logbook.
 *
 * @returns An object containing methods and properties for saving an entry:
 *   - `saveEntry`: A function to save the entry with provided credentials.
 *   - `loading`: A boolean indicating whether the save operation is in progress.
 *   - `opened`: A boolean indicating whether the save modal is open.
 *   - `open`: A function to open the save modal.
 *   - `close`: A function to close the save modal.
 *   - `usersSelect`: An array of users available for selection.
 *   - `disabled`: A boolean indicating whether the save operation is disabled.
 */
export const useSaveEntry = () => {
  const { post, isLoading } = usePostApi();
  const { upload, isUploading } = useUploadFileApi();
  const { logbookId, idType, lbeidOrPartId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const manualSave = true;
  const convertLbEntry = useConvertLbEntry(manualSave);
  const { pendingFiles } = usePendingFilesContext() as any;
  const { trigger, formState, getValues } = useFormContext();
  const isOnline = useIsOnline();
  const isNewEntry = useIsNewEntry();
  const isPendingWithdraw = useIsPendingWithdraw();
  const [_, setSearchParams] = useSearchParams();
  const { yachtid, _prename, entrydate } = useSelector(
    state => state.generatorSlice.entryDataToSave,
  );
  const { id, lbName } = useSelector(state => state.generatorSlice.latestEntry);
  const {
    users: defaultUsers,
    saveReason,
    bridgeLogUsers,
  } = useSelector(state => state.generatorSlice);
  const { current, noSaves, locked, you } = useSelector(
    state => state.entrySlice,
  );
  const sections = useSelector(state => state.generatorSlice.lbentry.sections);
  const { isGenericAccount } = useSelector(state => state.permsSlice);
  const opened = useSelector(state => state.entrySlice.openedSaveModal);
  const { saveContactNumber } = useSaveContactNumber();

  const users = useMemo(() => {
    if (isGenericAccount) {
      return bridgeLogUsers ?? [];
    }
    const usersSelect = defaultUsers;

    if (!usersSelect) return [];
    return mapUsers(usersSelect, "", "role firstname surname");
  }, [defaultUsers, bridgeLogUsers, isGenericAccount]);

  const onSaveSuccess = useCallback(
    async (res: any) => {
      dispatch(setNoSaves(noSaves + 1));
      dispatch(setActiveAutosave(false));
      dispatch(setReleaseEntryLock(Math.random()));
      let id = res[String(idType)] ?? "";
      let targetType = null;
      // Fix for missing Entry ID when id is NEW (fix for custom dashboard logs)
      if (!id) {
        if (res.lbeid) {
          id = res.lbeid;
          targetType = "lbeid";
        } else if (res.partid) {
          id = res.partid;
          targetType = "partid";
        } else if (res.meid) {
          id = res.meid;
          targetType = "meid";
        }
      }
      if (!id) return;

      // Save contact number after successful manual save
      await saveContactNumber();

      if (isNewEntry && pendingFiles.length > 0) {
        const body: Record<string, string> = {};
        body[String(idType)] = id;
        await upload("upload-file", pendingFiles, body, async res => {
          dispatch(setOpenedSaveModal(false));
          dispatch(setFiles(res.attachments));
          Notification(
            "Success",
            `The files has been added successfully`,
            "green",
          );
        });
      }
      if (targetType) {
        navigate(`/logbook/${logbookId}/${targetType}/${id}`, {
          replace: true,
        });
      }
      // dispatch(setWasSaved(true));
      navigate(`/logbook/${logbookId}/${idType}/${id}`, {
        replace: true,
      });
      refreshLogoutTimer();
      dispatch(setOpenedSaveModal(false));
      dispatch(refetchLogbook());
    },
    [
      dispatch,
      idType,
      isNewEntry,
      logbookId,
      navigate,
      pendingFiles,
      upload,
      noSaves,
      saveContactNumber,
    ],
  );
  const saveEntry = useCallback(
    async (credentials: ICredentials) => {
      const startTime = Date.now();
      const entryId =
        isNewEntry && !lbeidOrPartId?.includes("new=")
          ? "new"
          : (lbeidOrPartId ?? id);

      // Log manual save attempt
      crumb("Manual Save: Save entry initiated", {
        isNewEntry,
        entryId,
        logbookId,
        saveReason,
        userId: credentials.userid,
        hasFiles: pendingFiles.length > 0,
        filesCount: pendingFiles.length,
      });

      try {
        const res = await post({
          request: "save-entry",
          body: {
            params: {
              _current: current,
              entrydate,
              dto: getDateTimeRecord(),
              lbentry: convertLbEntry(),
              lbid: logbookId,
              ...credentials,
              yachtid,
              [String(idType)]: entryId,
              _prename,
              ...(saveReason !== ESaveEntryReason.none
                ? { reason: saveReason }
                : {}),
            },
            _lbid: logbookId,
            _lbname: lbName,
            ...(window?.api ? { _idInRoute: !!lbeidOrPartId } : {}),
          },
          onSuccess: onSaveSuccess,
        });

        if (res) {
          const duration = Date.now() - startTime;

          // Log successful manual save
          crumb("Manual Save: Save entry completed", {
            isNewEntry,
            entryId,
            logbookId,
            duration,
            status: res.status,
            success: true,
            hourlyRowsCount: sections[1]?.rows?.length ?? undefined,
          });

          dispatch(setReleaseEntryLock(Math.random()));
          dispatch(setCurrent(res.current || getCurrentUTC()));
        }

        dispatch(setSaveReason(ESaveEntryReason.none));
      } catch (error) {
        const duration = Date.now() - startTime;

        // Log failed manual save
        crumb("Manual Save: Save entry failed", {
          isNewEntry,
          entryId,
          logbookId,
          duration,
          error: error instanceof Error ? error.message : "Unknown error",
          success: false,
        });

        throw error;
      }
    },
    [
      isNewEntry,
      lbeidOrPartId,
      id,
      post,
      entrydate,
      convertLbEntry,
      logbookId,
      yachtid,
      idType,
      _prename,
      lbName,
      onSaveSuccess,
      current,
      saveReason,
    ],
  );
  const openModal = useCallback(async () => {
    // Trigger validation for all fields

    const result = await trigger();

    if (!result) {
      Notification("Error", "You need to fill all required fields", "red");
      return;
    }

    // if (!result) {
    //   // Retry validation after a brief delay to handle async state updates
    //   await new Promise(resolve => setTimeout(resolve, 100));
    //   const retryResult = await trigger();

    //   if (!retryResult) {
    //     const errors = formState.errors;
    //     const invalidFields = Object.keys(errors);

    //     // Filter out overlay field errors since they shouldn't block saves
    //     const nonOverlayErrors = invalidFields.filter(
    //       fieldName => !fieldName.startsWith("overlay_"),
    //     );

    //     if (nonOverlayErrors.length > 0) {
    //       throw Error("Fill in the required fields");
    //     }
    //   }
    // }

    dispatch(setOpenedSaveModal(true));
  }, [dispatch, trigger, formState.errors]);

  const closeModal = useCallback(() => {
    dispatch(setOpenedSaveModal(false));
  }, [dispatch]);

  const disabled = useMemo(
    () => isPendingWithdraw || (isOnline && locked && !you),
    [isOnline, isPendingWithdraw, locked, you],
  );

  return {
    saveEntry,
    loading: isLoading || isUploading,
    opened,
    open: openModal,
    close: closeModal,
    usersSelect: users,
    disabled,
  };
};
